'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_CATEGORY,
  DELETE_CATEGORY,
  GET_CATEGORY,
  GET_CATEGORIES,
  UPDATE_CATEGORY,
} from '../categoriesQueries'
import { DELETE_MANY_CONTENT_BLOCKS } from '../../ContentBlocks/contentBlockQueries'
import { Icons } from '@/components/shared/icons'
import { CategoryFormValues } from '../types'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { ContentBlockSelector } from '@/containers/ContentBlocks/components/ContentBlockSelector'
import { DraggableContentBlockList } from '@/containers/ContentBlocks/components/DraggableContentBlockList'
import InlineContentBlockForm from '@/containers/ContentBlocks/components/InlineContentBlockForm'
import { RemoveBlockDialog } from '@/containers/ContentBlocks/components/RemoveBlockDialog'
import { ContentBlockItem } from '@/containers/ContentBlocks/components/DraggableContentBlockItem'
import { useConfirmation } from '@/context/ConfirmationContext'
import { CategoryTreeSelect } from './CategoryTreeSelect'

const formSchema = z.object({
  category_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
        meta_information: z
          .object({
            id: z.number().optional(),
            permalink: z.string().optional(),
            meta_title: z.string().optional(),
            meta_description: z.string().optional(),
          })
          .optional(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  image_icon: z.array(z.string()).optional(),
  image: z.array(z.string()).optional(),
  banners: z.array(z.string()).optional(),
  parent_categoryId: z.union([z.number(), z.null()]).optional(),
  schema_href: z.string().optional(),
  code: z.string().optional(),
  selected_products: z.array(z.object({ id: z.number() })),
  show_in_menu: z.boolean(),
  is_root_category: z.boolean(),
  erp_synced: z.boolean(),
  menu_order: z.number().optional(),
  content_blocks: z.array(z.object({ id: z.number() })).optional(),
}) satisfies z.ZodType<CategoryFormValues>

interface CategoryFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function CategoryForm({
  data,
  onDirtyChange,
  onRequestClose,
}: CategoryFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)
  const [contentBlocks, setContentBlocks] = useState<ContentBlockItem[]>([])
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [inlineFormOpen, setInlineFormOpen] = useState(false)
  const [editingBlockId, setEditingBlockId] = useState<number | null>(null)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [blockToRemove, setBlockToRemove] = useState<ContentBlockItem | null>(null)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueCategory: categoryData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_CATEGORY, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      category_profiles: [],
      image_icon: [],
      image: [],
      banners: [],
      parent_categoryId: undefined,
      schema_href: '',
      code: '',
      selected_products: [],
      show_in_menu: false,
      is_root_category: false,
      erp_synced: false,
      menu_order: undefined,
      content_blocks: [],
    },
  })

  const [updateCategory] = useMutation(UPDATE_CATEGORY, {
    onError: e => {
      toast.error('Failed to update category')
    },
    onCompleted: () => {
      toast.success('Category updated successfully')
    },
    refetchQueries: ['GET_CATEGORIES', 'GET_CATEGORY', 'GET_CATEGORIES_TREE', 'GET_CATEGORIES_FOR_SELECT'],
  })

  const [createCategory] = useMutation(CREATE_CATEGORY, {
    onError: e => {
      toast.error('Failed to create category')
    },
    onCompleted: response => {
      toast.success('Category created successfully')
      const newId = response.createNewCategory.id
      setId(newId)
    },
    refetchQueries: ['GET_CATEGORIES', 'GET_CATEGORIES_TREE', 'GET_CATEGORIES_FOR_SELECT'],
  })

  const [deleteManyContentBlocks] = useMutation(DELETE_MANY_CONTENT_BLOCKS)

  const [deleteCategory] = useMutation(DELETE_CATEGORY, {
    refetchQueries: ['GET_CATEGORIES', 'GET_CATEGORY', 'GET_CATEGORIES_TREE', 'GET_CATEGORIES_FOR_SELECT'],
  })

  const handleDelete = () => {
    if (id) {
      const count = categoryData?.product_containers?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteCategory({
              variables: { where: { id } },
            })
            toast.success('Category deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete category')
          }
        },
        description: count
          ? `This category contains ${count} product(s). Deleting it will remove them from this category. This action cannot be undone.`
          : 'Are you sure you want to delete this category? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  const handleAddBlock = (block: ContentBlockItem) => {
    setContentBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
    setSelectorOpen(false)
  }

  const handleEditBlock = (blockId: number) => {
    setEditingBlockId(blockId)
    setInlineFormOpen(true)
  }

  const handleCreateNew = () => {
    setEditingBlockId(null)
    setInlineFormOpen(true)
  }

  const handleInlineCreated = (block: ContentBlockItem) => {
    setContentBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
  }

  const handleInlineUpdated = (block: ContentBlockItem) => {
    setContentBlocks(prev =>
      prev.map(b => (b.id === block.id ? { ...b, ...block, arrangement: b.arrangement } : b))
    )
  }

  const handleReorder = (reorderedBlocks: ContentBlockItem[]) => {
    setContentBlocks(reorderedBlocks)
  }

  const handleRemove = (blockId: number) => {
    const block = contentBlocks.find(b => b.id === blockId)
    if (block) {
      setBlockToRemove(block)
      setRemoveDialogOpen(true)
    }
  }

  const handleRemoveFromCategory = () => {
    if (blockToRemove) {
      setContentBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
      setBlockToRemove(null)
      setRemoveDialogOpen(false)
    }
  }

  const handleDeletePermanently = async () => {
    if (blockToRemove) {
      try {
        await deleteManyContentBlocks({ variables: { ids: [blockToRemove.id] } })
        setContentBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
        toast.success('Content block deleted permanently')
        setRemoveDialogOpen(false)
      } catch {
        toast.error('Failed to delete content block')
      }
      setBlockToRemove(null)
    }
  }

  const getBlockName = (block: ContentBlockItem) => {
    const profile = block.content_block_profiles.find(p => p.language === 'en')
    return profile?.name || block.content_block_profiles[0]?.name || `Block #${block.id}`
  }

  function onSubmit(values: CategoryFormValues) {
    if (id) {
      updateCategory({
        variables: {
          where: { id },
          category_profiles: values.category_profiles.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          image_icon: values.image_icon,
          image: values.image,
          banners: values.banners,
          parent_categoryId: values.parent_categoryId,
          schema_href: values.schema_href,
          code: values.code,
          selected_products: values.selected_products,
          show_in_menu: values.show_in_menu,
          is_root_category: values.is_root_category,
          erp_synced: values.erp_synced,
          menu_order: values.menu_order,
          content_blocks: contentBlocks.map(b => ({ id: b.id })),
        },
      })
    } else {
      createCategory({
        variables: {
          category_profiles: values.category_profiles.map(p => ({
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          image_icon: values.image_icon,
          image: values.image,
          banners: values.banners,
          parent_categoryId: values.parent_categoryId,
          schema_href: values.schema_href,
          code: values.code,
          selected_products: values.selected_products,
          show_in_menu: values.show_in_menu,
          is_root_category: values.is_root_category,
          erp_synced: values.erp_synced,
          menu_order: values.menu_order,
          content_blocks: contentBlocks.map(b => ({ id: b.id })),
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (categoryData) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          categoryData.category_profiles?.filter(
            p => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
              meta_information: {
                id: existingProfile.meta_information?.id,
                permalink: existingProfile.meta_information?.permalink ?? '',
                meta_title: existingProfile.meta_information?.meta_title ?? '',
                meta_description:
                  existingProfile.meta_information?.meta_description ?? '',
              },
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
              meta_information: {
                permalink: '',
                meta_title: '',
                meta_description: '',
              },
            }
          }
        })

        form.reset({
          category_profiles: allProfiles,
          image_icon: categoryData.image_icon
            ? [categoryData.image_icon.src]
            : [],
          image: categoryData.image ? [categoryData.image.src] : [],
          banners: categoryData.banners
            ? categoryData.banners.map(b => b.src)
            : [],
          parent_categoryId: categoryData.parent_categoryId,
          schema_href: categoryData.schema_href ?? '',
          code: categoryData.code ?? '',
          selected_products: categoryData.product_containers,
          show_in_menu: categoryData.show_in_menu ?? false,
          is_root_category: categoryData.is_root_category ?? false,
          erp_synced: categoryData.erp_synced ?? false,
          menu_order: categoryData.menu_order,
          content_blocks: (categoryData.content_blocks ?? []).map((b: { id: number }) => ({ id: b.id })),
        })

        // Initialize content blocks from category data
        if (categoryData.content_blocks) {
          setContentBlocks(
            categoryData.content_blocks.map((block: ContentBlockItem, index: number) => ({
              id: block.id,
              type: block.type,
              style: block.style || null,
              arrangement: block.arrangement ?? index,
              content_block_profiles: block.content_block_profiles || [],
              main_image: block.main_image || null,
              gallery_images: block.gallery_images || [],
              video_type: block.video_type || null,
              video_link: block.video_link || null,
              text_color: block.text_color || null,
              background_color: block.background_color || null,
              config: block.config ?? null,
              product_containers: block.product_containers || [],
              grid_product_containers: block.grid_product_containers || [],
              categories: block.categories || [],
            }))
          )
        } else {
          setContentBlocks([])
        }
      } else {
        // For NEW forms, initialize with profiles for all languages
        form.reset({
          category_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
            meta_information: {
              permalink: '',
              meta_title: '',
              meta_description: '',
            },
          })),
          image_icon: [],
          image: [],
          banners: [],
          parent_categoryId: undefined,
          schema_href: '',
          code: '',
          selected_products: [],
          show_in_menu: false,
          is_root_category: false,
          erp_synced: false,
          menu_order: undefined,
          content_blocks: [],
        })
        setContentBlocks([])
      }
    }
  }, [categoryData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Category</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Category Name"
                description="This is your public display name."
                control={form.control}
                name={`category_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                description="Stable technical identifier used for ERP integration/sync. Entered manually and not shown to customers."
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="e.g. CAT-001" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`category_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Title"
                control={form.control}
                name={`category_profiles.${languageId}.meta_information.meta_title`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Description"
                control={form.control}
                name={`category_profiles.${languageId}.meta_information.meta_description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Meta Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Permalink"
                control={form.control}
                name={`category_profiles.${languageId}.meta_information.permalink`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Icon"
                description="Upload category icon"
                control={form.control}
                name="image_icon"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Image"
                description="Upload category image"
                control={form.control}
                name="image"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Banners"
                description="Upload category banners"
                control={form.control}
                name="banners"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={5} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Parent Category"
                control={form.control}
                name="parent_categoryId"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <CategoryTreeSelect
                        value={field.value ?? null}
                        onChange={(val) => field.onChange(val)}
                        excludeId={id ?? undefined}
                        placeholder="No parent (root category)"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Schema Href"
                control={form.control}
                name="schema_href"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Schema href" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Show in Menu"
                control={form.control}
                name="show_in_menu"
                variant="checkbox"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Root Category"
                control={form.control}
                name="is_root_category"
                variant="checkbox"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Managed by ERP import"
                control={form.control}
                name="erp_synced"
                variant="checkbox"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormDescription>
                      When on, the nightly ERP import removes products from this
                      category if AX no longer assigns them here. Turn off for
                      categories maintained by hand. The import turns this back
                      on for categories it receives from AX.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Menu Order"
                control={form.control}
                name="menu_order"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Menu order"
                        {...field}
                        onChange={e =>
                          field.onChange(
                            e.target.value
                              ? parseInt(e.target.value)
                              : undefined
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="border-t pt-4 mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Content Blocks</h3>
                  <Button type="button" onClick={() => setSelectorOpen(true)}>
                    Add Block
                  </Button>
                </div>

                {contentBlocks.length > 0 ? (
                  <DraggableContentBlockList
                    blocks={contentBlocks}
                    onReorder={handleReorder}
                    onRemove={handleRemove}
                    onEdit={handleEditBlock}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">No content blocks assigned to this category.</p>
                )}

                <ContentBlockSelector
                  open={selectorOpen}
                  onOpenChange={setSelectorOpen}
                  assignedBlockIds={contentBlocks.map(b => b.id)}
                  onSelect={handleAddBlock}
                  onCreateNew={handleCreateNew}
                />

                <InlineContentBlockForm
                  open={inlineFormOpen}
                  onOpenChange={setInlineFormOpen}
                  blockId={editingBlockId}
                  onCreated={handleInlineCreated}
                  onUpdated={handleInlineUpdated}
                />

                <RemoveBlockDialog
                  open={removeDialogOpen}
                  onOpenChange={setRemoveDialogOpen}
                  blockName={blockToRemove ? getBlockName(blockToRemove) : ''}
                  onRemoveFromPage={handleRemoveFromCategory}
                  onDeletePermanently={handleDeletePermanently}
                />
              </div>

              <div className="flex gap-2">
                {id && (
                  <Button type="button" variant="outline" size="icon" onClick={handleDelete} className="shrink-0">
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button type="submit" disabled={loadingData} className="flex-1">
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
